import { VNode, createVNode, render as vueRender } from 'vue'
import { omit } from 'lodash'
import { isVNode, isString } from '@sgui-plus/utils/util'
import ModalVue from './modal.vue'
import type { ModalFnProps, ModalParams } from './modal'

type ModalQueueItem = {
  vm: VNode
  destroy?: any
  update?: any
  resolve?: (res: any) => void
  reject?: (reason?: any) => void
}

const instances: ModalQueueItem[] = []

let seed = 1

const confirm = (config: any, extras?: ModalParams) => {
  if (isString(config) || isVNode(config)) {
    config = { content: config }
  }

  Object.assign(config, extras)

  const container = document.createDocumentFragment()
  const id = `modal_${seed++}`

  let currentConfig = {
    id,
    ...omit(config, ['appContext']),
    modelValue: true,
    transfer: true,
    onOk: () => {
      if (typeof config.onOk === 'function') {
        config.onOk(close)
      }
    },
    onHidden: () => {
      if (typeof config.onHidden === 'function') {
        config.onHidden()
      }
      destroy.apply(typeof close)
    }
  } as ModalFnProps

  let modalInstance: VNode|null = null

  function destroy() {
    if (modalInstance) {
      vueRender(null, container as any)
      modalInstance.component?.update()
      modalInstance = null
    }

    for (let i = 0; i < instances.length; i++) {
      const instance = instances[i]
      if (instance.destroy === close) {
        instances.splice(i, 1)
        break
      }
    }
  }

  function close() {
    update({ modelValue: false })
  }

  function update(configUpdate) {
    if (typeof configUpdate === 'function') {
      currentConfig = configUpdate(currentConfig)
    } else {
      currentConfig = {
        ...currentConfig,
        ...configUpdate
      }
    }
    if (modalInstance) {
      // @ts-ignore
      Object.assign(modalInstance.component.props, currentConfig)
      modalInstance.component?.update()
      // $forceUpdate 方法在构建后不可用
      // @ts-ignore
      // modalInstance.component.ctx.$forceUpdate()
    }
  }

  function Wrapper (props: ModalFnProps) {
    return createVNode(ModalVue, { ...props }, { default: () => props.content })
  }

  function render(props: ModalFnProps) {
    // const vm = createVNode(ModalVue, { ...props }, { default: () => props.content })
    const vm = createVNode(Wrapper, { ...props })
    vm.appContext = config.appContext || vm.appContext
    vueRender(vm, container as any)
    return vm
  }

  modalInstance = render(currentConfig)

  instances.push({ vm: modalInstance, destroy: close, update })

  return {
    destroy: close,
    update
  }
}

ModalVue.confirm = (options: ModalParams = {}) => {
  return confirm(options, {
    modalType: 'confirm',
    promptType: 'confirm'
  })
}

ModalVue.alert = (options: ModalParams = {}) => {
  return confirm(options, {
    modalType: 'alert',
    showCancel: false
  })
}

ModalVue.info = (options: ModalParams = {}) => {
  return confirm(options, {
    promptType: 'info'
  })
}

ModalVue.success = (options: ModalParams = {}) => {
  return confirm(options, {
    promptType: 'success'
  })
}

ModalVue.warning = (options: ModalParams = {}) => {
  return confirm(options, {
    promptType: 'warning'
  })
}

ModalVue.error = (options: ModalParams = {}) => {
  return confirm(options, {
    promptType: 'error'
  })
}

ModalVue.show = (options = {}) => {
  return confirm(options)
}

ModalVue.remove = (id: string) => {
  if (instances.length === 0) {
    return
  }

  const index = instances.findIndex(item => item.vm.props?.id === id)

  if (index < 0) {
    return
  }

  instances[index].destroy()
}

ModalVue.closeAll = () => {
  for (let i = instances.length - 1; i >= 0; i--) {
    instances[i].destroy()
  }
}

export default ModalVue
